import { Box, SxProps, Theme } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import useImage from "use-image";

import { useBoundingBoxesContext } from "context/boundingBoxesContext";
import { useImageSettingsContext } from "context/ImageSettingsContext/imageSettingsContext";
import { getCanvasAnnotationsFromROIs } from "features/canvas/utils";
import useBoxLabelAndStepData from "hooks/useSetBBoxesData/useSetBBoxesData";
import { Detector } from "types/detectors";
import { ROI } from "types/imageAnnotations";
import { DisplayBBoxInfo, ImageQuery } from "types/imageQueries";
import { LabelValue } from "types/labelValue";
import { generateImageUrl } from "utils/generateImageUrl";

import { FullscreenGroundlightImageQuery } from "./FullscreenGroundlightImageQuery";
import { GroundlightImageQueryContent } from "./GroundlightImageQueryContent";
import { UnsavedAnnotationsConfirmationModal } from "./UnsavedAnnotationsConfirmationModal";

type GroundlightImageQueryContextConnectedProps = {
  data?: ImageQuery;
  loading?: boolean;
  labelRows?: LabelValue[];
  detector?: Detector;
  sx?: SxProps<Theme>;
  onClick?: () => void;
  onSendLabel?: () => Promise<void>;
  isReview?: boolean;
  enablePanZoom?: boolean;
  hideToolbar?: boolean;
  hideAnswerPanel?: boolean;
  onUnsavedAnnotationsHandled?: (confirmed: boolean) => void;
  showUnsavedAnnotationsWarning?: boolean;
  reviewMode: boolean;
  reviewBBoxInfo?: DisplayBBoxInfo;
  roisToDisplay?: ROI[] | null;
  selectedLabelValue?: LabelValue | null;
};

export function GroundlightImageQueryContextConnected(props: GroundlightImageQueryContextConnectedProps) {
  const {
    onClick,
    detector,
    data,
    hideToolbar,
    hideAnswerPanel,
    enablePanZoom,
    onSendLabel,
    labelRows,
    loading,
    onUnsavedAnnotationsHandled,
    showUnsavedAnnotationsWarning,
    reviewMode,
    roisToDisplay,
    selectedLabelValue,
    reviewBBoxInfo,
  } = props;

  const cursorStyle = onClick !== undefined ? "pointer" : undefined;
  const imageData = useImage(data ? generateImageUrl(data.iq_id) : "");

  const { fullScreen, setAnnotations, setValidBoxLabels, validBoxLabels, setStepsData, isBoundingBoxes } =
    useBoundingBoxesContext();
  const { isEditing } = useImageSettingsContext();

  const canvasAnnotations = useMemo(
    () => (roisToDisplay ? getCanvasAnnotationsFromROIs(roisToDisplay) : []),
    [roisToDisplay]
  );

  // Hook to set data and steps
  useBoxLabelAndStepData({
    data,
    detector,
    setStepsData,
    setValidBoxLabels,
    validBoxLabels,
    isBoundingBoxes,
    rois: canvasAnnotations,
  });

  useEffect(() => {
    if (isBoundingBoxes) {
      // This will help us to enable the boxes with the label
      setStepsData((stepsData) => ({ ...stepsData, currentStep: 1 }));
    } else {
      setAnnotations(canvasAnnotations);
    }
  }, [isBoundingBoxes, canvasAnnotations, setAnnotations, setStepsData]);

  const [initializedAnnotations, setInitializedAnnotations] = useState(false);

  useEffect(() => {
    if (!initializedAnnotations) {
      setAnnotations(canvasAnnotations);
      setInitializedAnnotations(true);
    }
  }, [setAnnotations, initializedAnnotations, canvasAnnotations]);

  useEffect(() => {
    if (!isEditing) {
      setAnnotations(canvasAnnotations);
    }
  }, [isEditing, setAnnotations, canvasAnnotations]);

  // Create a single instance of content props that can be used in both places
  const contentProps = {
    detector,
    data,
    imageData,
    hideToolbar,
    hideAnswerPanel,
    enablePanZoom,
    enableAnnotation: isBoundingBoxes,
    style: {
      cursor: cursorStyle,
    },
    loading,
    onSendLabel,
    labelRows,
    reviewMode,
    selectedLabelValue,
    reviewBBoxInfo,
  };

  return (
    <>
      {!fullScreen && (
        <Box
          id="groundlight-image-query"
          sx={{
            height: "100%",
            width: "100%",
            cursor: cursorStyle,
          }}
          onClick={onClick}
        >
          <GroundlightImageQueryContent {...contentProps} />
        </Box>
      )}
      <FullscreenGroundlightImageQuery>
        {fullScreen && <GroundlightImageQueryContent {...contentProps} title={data?.query} />}
      </FullscreenGroundlightImageQuery>

      {showUnsavedAnnotationsWarning && (
        <UnsavedAnnotationsConfirmationModal data={data} onUnsavedAnnotationsHandled={onUnsavedAnnotationsHandled} />
      )}
    </>
  );
}
